/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from 'componentregistry';
import { memo, useEffect } from 'react';
import useTrigger from 'triggers/useTrigger';
import { startPageProgress } from './PageProgressed';
import {
  canvasGridIconStateAtom,
  canvasGridTooltipAtom,
} from 'components/CanvasGrid/state';

interface Props {
  paramString?: string;
  appUid: string;
  roleUid: string;
  pageId: string;
  itemId: string;
  platform: Platform;
}

const pageActiveTriggered = new Map<string, boolean>();

function PageTriggers(props: Props) {
  const { appUid, roleUid, itemId, pageId, paramString, platform } = props;

  const [onPageActiveTrigger] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnPageActive',
  );
  const [onPageExitTrigger] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnPageExit',
  );

  const key = `${appUid}-${pageId}-${paramString}`;
  if (!pageActiveTriggered.has(key)) {
    pageActiveTriggered.set(key, true);
    const end = startPageProgress(appUid, pageId, `TriggerOnPageActive-${key}`);
    if (onPageActiveTrigger) {
      setTimeout(() => {
        onPageActiveTrigger(undefined, {}).finally(end);
      }, 1);
    } else {
      end();
    }
  }

  useEffect(() => {
    return () => {
      pageActiveTriggered.delete(key);
      onPageExitTrigger?.(undefined, {});
      platform.remover(canvasGridTooltipAtom.path.slice(0, -1), {
        params: { appUid, pageId },
      });
      platform.remover(canvasGridIconStateAtom.path.slice(0, -2), {
        params: { appUid, pageId },
      });
    };
  }, [onPageActiveTrigger, onPageExitTrigger, key, platform, appUid, pageId]);

  return null;
}

export default memo(PageTriggers);
